import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';

export default function ProjectOptimization({ board, api, isOpen, onClose }) {
  const [optimization, setOptimization] = useState({
    caching: false,
    preloading: false,
    compression: false,
    lazy_loading: true,
    auto_sync: true,
    performance_mode: false
  });
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);

  useEffect(() => {
    if (isOpen && board) {
      fetchOptimizationSettings();
      analyzePerformance();
    }
  }, [isOpen, board]);

  const fetchOptimizationSettings = async () => {
    try {
      const response = await api.get(`/projects/boards/${board.id}/optimization`);
      setOptimization(response.data || optimization);
    } catch (error) {
      console.error('Error fetching optimization settings:', error);
    }
  };

  const analyzePerformance = async () => {
    setAnalyzing(true);
    try {
      const response = await api.get(`/projects/boards/${board.id}/performance-analysis`);
      setMetrics(response.data);
    } catch (error) {
      console.error('Error analyzing performance:', error);
      // Mock data for demonstration
      setMetrics({
        load_time: 1.2,
        memory_usage: 45,
        cache_hit_rate: 78,
        api_response_time: 320,
        bundle_size: 2.1,
        recommendations: [
          {
            id: 'cache_enable',
            title: 'Ativar Cache Inteligente',
            description: 'Reduzir carregamento de dados repetidos em 60%',
            impact: 'high',
            potential_improvement: '40% mais rápido'
          },
          {
            id: 'lazy_loading',
            title: 'Carregamento Sob Demanda',
            description: 'Carregar componentes apenas quando necessário',
            impact: 'medium',
            potential_improvement: '25% menos memória'
          },
          {
            id: 'compression',
            title: 'Compressão de Dados',
            description: 'Reduzir tamanho das transferências de dados',
            impact: 'high',
            potential_improvement: '50% menos tráfego'
          }
        ],
        performance_score: 72
      });
    } finally {
      setAnalyzing(false);
    }
  };

  const updateOptimization = async (key, value) => {
    setLoading(true);
    try {
      const newSettings = { ...optimization, [key]: value };
      
      await api.put(`/projects/boards/${board.id}/optimization`, newSettings);
      
      setOptimization(newSettings);
      toast.success(`${getSettingName(key)} ${value ? 'ativado' : 'desativado'}`);
      
      // Re-analyze performance after change
      setTimeout(analyzePerformance, 1000);
    } catch (error) {
      console.error('Error updating optimization:', error);
      toast.error('Erro ao atualizar configuração');
    } finally {
      setLoading(false);
    }
  };

  const applyRecommendation = async (recommendationId) => {
    setLoading(true);
    try {
      await api.post(`/projects/boards/${board.id}/apply-optimization`, {
        recommendation: recommendationId
      });
      
      toast.success('Otimização aplicada com sucesso!');
      
      // Update settings based on recommendation
      switch (recommendationId) {
        case 'cache_enable':
          setOptimization(prev => ({ ...prev, caching: true }));
          break;
        case 'lazy_loading':
          setOptimization(prev => ({ ...prev, lazy_loading: true }));
          break;
        case 'compression':
          setOptimization(prev => ({ ...prev, compression: true }));
          break;
      }
      
      analyzePerformance();
    } catch (error) {
      console.error('Error applying recommendation:', error);
      toast.error('Erro ao aplicar otimização');
    } finally {
      setLoading(false);
    }
  };

  const getSettingName = (key) => {
    const names = {
      caching: 'Cache Inteligente',
      preloading: 'Pré-carregamento',
      compression: 'Compressão de Dados',
      lazy_loading: 'Carregamento Sob Demanda',
      auto_sync: 'Sincronização Automática',
      performance_mode: 'Modo Performance'
    };
    return names[key] || key;
  };

  const getSettingDescription = (key) => {
    const descriptions = {
      caching: 'Armazena dados frequentes em memória para acesso mais rápido',
      preloading: 'Carrega dados antecipadamente para melhorar a experiência',
      compression: 'Comprime dados durante transferência para reduzir uso de banda',
      lazy_loading: 'Carrega componentes apenas quando necessário',
      auto_sync: 'Mantém dados sincronizados automaticamente em tempo real',
      performance_mode: 'Prioriza performance sobre recursos visuais avançados'
    };
    return descriptions[key] || '';
  };

  const getImpactColor = (impact) => {
    const colors = {
      high: 'bg-red-500',
      medium: 'bg-yellow-500',
      low: 'bg-green-500'
    };
    return colors[impact] || 'bg-gray-500';
  };

  const getImpactLabel = (impact) => {
    const labels = {
      high: 'Alto Impacto',
      medium: 'Médio Impacto',
      low: 'Baixo Impacto'
    };
    return labels[impact] || impact;
  };

  const getPerformanceColor = (score) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 70) return 'text-yellow-600';
    return 'text-red-600';
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="flex h-[92vh] max-h-[92vh] w-[calc(100vw-1rem)] max-w-6xl flex-col overflow-hidden p-0 sm:w-[calc(100vw-2rem)]">
        <DialogHeader className="shrink-0 border-b border-slate-200 px-4 py-4 sm:px-6 sm:py-5">
          <DialogTitle className="flex flex-wrap items-center gap-2">
            <span className="text-2xl">⚡</span>
            <span>Otimização de Performance</span>
            <Badge variant="outline" className="ml-2">
              {board.name}
            </Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6 sm:py-6">
          <div className="space-y-6">
            
            {/* Performance Overview */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <span>📊</span>
                  <span>Análise de Performance</span>
                  {analyzing && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {metrics ? (
                  <div className="space-y-6">
                    {/* Performance Score */}
                    <div className="text-center">
                      <div className={`text-4xl font-bold ${getPerformanceColor(metrics.performance_score)}`}>
                        {metrics.performance_score}/100
                      </div>
                      <div className="text-sm text-gray-600 mt-1">Score de Performance</div>
                      <Progress 
                        value={metrics.performance_score} 
                        className="mt-2 h-2"
                      />
                    </div>

                    {/* Metrics Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      <div className="text-center p-3 bg-gray-50 rounded-lg">
                        <div className="text-2xl font-bold text-blue-600">{metrics.load_time}s</div>
                        <div className="text-xs text-gray-600">Tempo de Carga</div>
                      </div>
                      <div className="text-center p-3 bg-gray-50 rounded-lg">
                        <div className="text-2xl font-bold text-green-600">{metrics.memory_usage}%</div>
                        <div className="text-xs text-gray-600">Uso de Memória</div>
                      </div>
                      <div className="text-center p-3 bg-gray-50 rounded-lg">
                        <div className="text-2xl font-bold text-purple-600">{metrics.cache_hit_rate}%</div>
                        <div className="text-xs text-gray-600">Taxa de Cache</div>
                      </div>
                      <div className="text-center p-3 bg-gray-50 rounded-lg">
                        <div className="text-2xl font-bold text-orange-600">{metrics.api_response_time}ms</div>
                        <div className="text-xs text-gray-600">Tempo API</div>
                      </div>
                      <div className="text-center p-3 bg-gray-50 rounded-lg">
                        <div className="text-2xl font-bold text-red-600">{metrics.bundle_size}MB</div>
                        <div className="text-xs text-gray-600">Tamanho Bundle</div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p>Analisando performance...</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Optimization Settings */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <span>⚙️</span>
                  <span>Configurações de Otimização</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {Object.entries(optimization).map(([key, enabled]) => (
                    <div key={key} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <Label className="font-medium">{getSettingName(key)}</Label>
                          <Badge variant={enabled ? 'default' : 'secondary'} className="text-xs">
                            {enabled ? 'Ativo' : 'Inativo'}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          {getSettingDescription(key)}
                        </p>
                      </div>
                      <Switch
                        checked={enabled}
                        onCheckedChange={(checked) => updateOptimization(key, checked)}
                        disabled={loading}
                      />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Recommendations */}
            {metrics && metrics.recommendations && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <span>💡</span>
                    <span>Recomendações de Otimização</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {metrics.recommendations.map((rec) => (
                      <div key={rec.id} className="flex items-center justify-between p-4 border rounded-lg">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <h4 className="font-medium">{rec.title}</h4>
                            <Badge 
                              className={`text-xs text-white ${getImpactColor(rec.impact)}`}
                            >
                              {getImpactLabel(rec.impact)}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-600">{rec.description}</p>
                          <div className="text-sm font-medium text-green-600 mt-1">
                            📈 {rec.potential_improvement}
                          </div>
                        </div>
                        <Button 
                          size="sm"
                          onClick={() => applyRecommendation(rec.id)}
                          disabled={loading}
                        >
                          Aplicar
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Performance Tips */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <span>🎯</span>
                  <span>Dicas de Performance</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div className="flex items-start space-x-2">
                      <span className="text-green-500">✅</span>
                      <div className="text-sm">
                        <div className="font-medium">Mantenha dados organizados</div>
                        <div className="text-gray-600">Grupos e filtros bem estruturados melhoram o desempenho</div>
                      </div>
                    </div>
                    <div className="flex items-start space-x-2">
                      <span className="text-green-500">✅</span>
                      <div className="text-sm">
                        <div className="font-medium">Use paginação</div>
                        <div className="text-gray-600">Limite a quantidade de itens exibidos por vez</div>
                      </div>
                    </div>
                    <div className="flex items-start space-x-2">
                      <span className="text-green-500">✅</span>
                      <div className="text-sm">
                        <div className="font-medium">Otimize imagens</div>
                        <div className="text-gray-600">Comprima anexos e avatares para carregamento mais rápido</div>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-start space-x-2">
                      <span className="text-yellow-500">⚠️</span>
                      <div className="text-sm">
                        <div className="font-medium">Evite muitas integrações ativas</div>
                        <div className="text-gray-600">Desative integrações não utilizadas</div>
                      </div>
                    </div>
                    <div className="flex items-start space-x-2">
                      <span className="text-yellow-500">⚠️</span>
                      <div className="text-sm">
                        <div className="font-medium">Monitore o uso de memória</div>
                        <div className="text-gray-600">Feche abas não utilizadas do projeto</div>
                      </div>
                    </div>
                    <div className="flex items-start space-x-2">
                      <span className="text-yellow-500">⚠️</span>
                      <div className="text-sm">
                        <div className="font-medium">Limpe dados antigos</div>
                        <div className="text-gray-600">Archive projetos concluídos regularmente</div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <div className="flex justify-between">
              <Button variant="outline" onClick={analyzePerformance} disabled={analyzing}>
                {analyzing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                    Analisando...
                  </>
                ) : (
                  <>
                    <span className="mr-2">🔄</span>
                    Nova Análise
                  </>
                )}
              </Button>
              <Button onClick={onClose}>
                Fechar
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
